/**
 * A11-003 (keyboard-only donor journey) — whose Escape is it?
 *
 * Rule: Escape closes the widget overlay unless the key went to a control whose
 * Escape-dismissable popup is open: a combobox, or a control with
 * `aria-haspopup`, that is `aria-expanded="true"`, or a control that owns (via
 * `aria-controls` / `aria-owns`) a rendered listbox, menu or dialog. That
 * Escape closes the popup instead.
 *
 * Why: the overlay's window listener closed the whole overlay on any Escape, so
 * a donor closing a multi-select question's menu lost the form. The check is
 * limited to popups on purpose: `aria-expanded` alone is also how a disclosure
 * such as an FAQ accordion says it is open, and Escape does not close those.
 * react-select calls preventDefault on every Escape, open or not, so
 * `defaultPrevented` cannot decide it either. The overlay takes this decision
 * in the CAPTURE phase, before the popup's own handler closes it.
 */

const POPUP_TRIGGER =
  '[role="combobox"][aria-expanded="true"], [aria-haspopup]:not([aria-haspopup="false"])[aria-expanded="true"]';
const POPUP_ROLES = new Set(['listbox', 'menu', 'dialog']);
const FLOATING_POPUP = '[data-radix-popper-content-wrapper]';

function ownsRenderedPopup(control) {
  const doc = control.ownerDocument;
  const ids = `${control.getAttribute('aria-controls') || ''} ${control.getAttribute('aria-owns') || ''}`
    .split(/\s+/)
    .filter(Boolean);
  return ids.some((id) => {
    const popup = doc.getElementById(id);
    return Boolean(popup && POPUP_ROLES.has(popup.getAttribute('role')) && popup.getClientRects().length > 0);
  });
}

/** True when this Escape belongs to an open popup rather than to the overlay. */
export function escapeBelongsToOpenPopup(event) {
  if (event.key !== 'Escape') return false;
  const target = event.target;
  if (!target?.closest) return false;
  if (target.closest(POPUP_TRIGGER)) return true;
  // Focus inside a floating popup itself (the schedule's time listbox, a calendar), portaled
  // away from its trigger: that popup closes on this Escape, not the overlay.
  if (target.closest(FLOATING_POPUP)) return true;
  const owner = target.closest('[aria-controls], [aria-owns]');
  return Boolean(owner && ownsRenderedPopup(owner));
}
